import { DateTime, Duration } from "luxon"
import { useCallback, useMemo, useRef, useState } from "react"

import { Logger } from "../../../../../../../lib/logging"
import { sleep } from "../../../../../../../lib/lang"
import { useInterval } from "../../../../../../../shared/hooks/useInterval"
import { useEffectOnce } from "../../../../../../../shared/hooks/useEffectOnce"
import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { fetchOrderStatus } from "../../../../../../../shared/components/createOrder/createOrderApi"
import { isPaymentSuccess } from "../../../../../../payments/transactionTypes"
import { PaymentErrorType, PaymentState, PaymentStatus, ProgressState } from "../types/paymentTypes"
import { AppError } from "../../../../../../../core/api/errorTypes"

const DEFAULT_POLL_INTERVAL_IN_MILLIS = 500
const DEFAULT_MAX_POLLING_TIME_IN_SECONDS = 30

export function usePaymentFlow() {
    const mounted = useRef(true)

    const [paymentState, setPaymentState] = useState<PaymentState>({
        status: PaymentStatus.PENDING,
        final: false,
    })

    const { mutateAsync: getOrderStatus } = useApiMutation(fetchOrderStatus, {})

    const timeout = useMemo(
        () =>
            Duration.fromObject({
                seconds: DEFAULT_MAX_POLLING_TIME_IN_SECONDS,
            }),
        [],
    )

    const [progressState, setProgressState] = useState<ProgressState>()

    const pollForPaymentState = useCallback(
        async (orderUid: string) => {
            const logger = new Logger("payment-flow")

            const startTime = DateTime.now()
            const endTime = startTime.plus(timeout)
            let done = false

            logger.info(
                `Start polling for payment state [startTime: ${startTime.toISO()}, endTime: ${endTime.toISO()}, timeout: ${timeout.toISO()}]`,
            )
            setPaymentState({
                status: PaymentStatus.IN_PROGRESS,
                final: false,
            })
            setProgressState({
                startTime,
                endTime,
                remaining: timeout,
                progress: 100,
            })

            while (mounted.current && !done) {
                try {
                    const currentTime = DateTime.now()
                    // Check if the polling timeout has been reached
                    if (currentTime >= endTime) {
                        logger.info("Timeout during payment polling [show error]")
                        setPaymentState({
                            status: PaymentStatus.FAILED,
                            final: true,
                            errorType: PaymentErrorType.TIMEOUT,
                            error: undefined,
                        })
                        done = true
                        continue // skip poll
                    }

                    logger.info("Fetching payment status")
                    const response = await getOrderStatus({ orderUid })

                    if (isPaymentSuccess(response)) {
                        logger.info("Payment complete")
                        setPaymentState({
                            status: PaymentStatus.COMPLETED,
                            final: true,
                            response,
                        })
                        done = true
                        continue // skip sleep
                    }

                    await sleep(DEFAULT_POLL_INTERVAL_IN_MILLIS)
                } catch (err: unknown) {
                    logger.error("Payment polling failed", err)
                    setPaymentState({
                        status: PaymentStatus.FAILED,
                        final: true,
                        errorType: PaymentErrorType.QUERY_ERROR,
                        error: AppError.wrap(err).toObject(),
                    })
                    done = true
                }
            }
        },
        [setPaymentState, getOrderStatus],
    )

    useInterval(
        () => {
            setProgressState((prev) => {
                if (!prev) return prev

                const { startTime, endTime } = prev
                const now = DateTime.now()
                const zeroDuration = Duration.fromMillis(0)

                const totalMillis = timeout.as("milliseconds")
                const timeLeftInMillis = endTime.diff(now, "milliseconds").as("milliseconds")
                const remainingPercentage = (timeLeftInMillis / totalMillis) * 100

                return {
                    startTime,
                    endTime,
                    remaining: endTime > now ? endTime.diff(now, "seconds") : zeroDuration,
                    progress: remainingPercentage,
                }
            })
        },
        paymentState.status === PaymentStatus.IN_PROGRESS ? 500 : null,
    )

    useEffectOnce(() => {
        return () => {
            const logger = new Logger("payment")
            logger.info("Payment components unmounted [stop polling]")
            mounted.current = false
        }
    })

    return {
        paymentState,
        progressState,
        pollForPaymentState,
    }
}
